import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Avatar,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  useTheme,
} from '@mui/material';
import { Grid } from '../components/common/TypedGrid';
import {
  TrendingUp,
  People,
  Assignment,
  AttachMoney,
  PersonAdd,
  Refresh,
  MoreVert,
} from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useAdmin } from '../contexts/AdminContext';
import { PageHeader, SectionCard, StatCard, StatusChip } from '../components/common';
import { heroGlow } from '../theme';
import adminService, { NinjaProfile, DeliveryTask } from '../services/adminService';
import { db } from '../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

// Issue #212: Service segment labels for fallback mapping
const serviceSegmentLabels: { [key: string]: string } = {
  delivery: 'Delivery Services',
  home_services: 'Home Services',
  personal_care: 'Personal Care',
  professional: 'Professional Services',
  transportation: 'Transportation',
};

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const { dashboardStats, refreshDashboardStats } = useAdmin();
  const [loadError, setLoadError] = useState<string | null>(null);
  const [recentNinjas, setRecentNinjas] = useState<NinjaProfile[]>([]);
  const [recentTasks, setRecentTasks] = useState<DeliveryTask[]>([]);
  const [loading, setLoading] = useState(true);

  // Issue #212: Service segment name mapping
  const [serviceSegmentsMap, setServiceSegmentsMap] = useState<{ [key: string]: string }>({});
  const [serviceCategoriesMap, setServiceCategoriesMap] = useState<{ [key: string]: string }>({});

  // Dynamic data states
  const [revenueData, setRevenueData] = useState<Array<{ name: string; revenue: number }>>([]);
  const [taskStatusData, setTaskStatusData] = useState<Array<{ name: string; value: number; color: string }>>([]);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState('6months');
  const [performanceMetrics, setPerformanceMetrics] = useState<{
    revenueGrowth: number;
    taskGrowth: number;
    ninjaGrowth: number;
    customerGrowth: number;
  }>({
    revenueGrowth: 0,
    taskGrowth: 0,
    ninjaGrowth: 0,
    customerGrowth: 0,
  });

  // Issue #212: Load service segments and categories for name mapping
  useEffect(() => {
    const loadServiceData = async () => {
      try {
        // Fetch service segments
        const segmentsRef = collection(db, 'service_segments');
        const segmentsQuery = query(segmentsRef, where('isActive', '==', true));
        const segmentsSnapshot = await getDocs(segmentsQuery);
        const segmentsMap: { [key: string]: string } = {};
        segmentsSnapshot.forEach((doc) => {
          const data = doc.data();
          segmentsMap[doc.id] = data.name || doc.id;
        });
        setServiceSegmentsMap(segmentsMap);

        // Fetch service categories
        const categoriesRef = collection(db, 'service_categories');
        const categoriesQuery = query(categoriesRef, where('isActive', '==', true));
        const categoriesSnapshot = await getDocs(categoriesQuery);
        const categoriesMap: { [key: string]: string } = {};
        categoriesSnapshot.forEach((doc) => {
          const data = doc.data();
          categoriesMap[doc.id] = data.name || doc.id;
        });
        setServiceCategoriesMap(categoriesMap);
      } catch (error) {
        console.error('Error loading service data:', error);
      }
    };
    loadServiceData();
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [analyticsTimeRange]);

  // Issue #212: Helper function to convert segment IDs to human-readable names
  const getSegmentNames = (segments: string[]): string => {
    if (!segments || segments.length === 0) return 'No services';
    return segments.map(segmentId =>
      serviceSegmentsMap[segmentId] ||
      serviceCategoriesMap[segmentId] ||
      serviceSegmentLabels[segmentId] ||
      segmentId
    ).join(', ');
  };

  const loadDashboardData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Issue #358: these two lists show five rows each. Fetching five rows
      // costs five document reads; getAllTasks() was fetching every task ever
      // created — 5,000 reads and ~445ms on a 5,000-task collection — to then
      // throw all but five away.
      const [ninjas, recentTasks, analyticsData, growthMetrics] = await Promise.all([
        adminService.getPendingNinjas(),
        adminService.getRecentTasks(5),
        adminService.getAnalyticsData(analyticsTimeRange),
        adminService.getGrowthMetrics(),
      ]);
      
      setRecentNinjas(ninjas.slice(0, 5));
      setRecentTasks(recentTasks);
      
      // Transform analytics data for charts
      setRevenueData(analyticsData.revenue.map(item => ({
        name: item.month,
        revenue: item.revenue
      })));
      
      setTaskStatusData(analyticsData.tasksByStatus.map(item => ({
        name: item.status,
        value: item.count,
        color: item.color
      })));
      
      // Set real growth metrics
      setPerformanceMetrics(growthMetrics);
      
      await refreshDashboardStats();
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Fall back to basic data if analytics fail. This path performs its own
      // network calls, so it needs its own guard — otherwise a second failure
      // escapes as an unhandled rejection and the page silently shows zeros.
      try {
        const [ninjas, recentTasks] = await Promise.all([
          adminService.getPendingNinjas(),
          adminService.getRecentTasks(5),
        ]);

        setRecentNinjas(ninjas.slice(0, 5));
        setRecentTasks(recentTasks);

        // Fallback task distribution using dashboard stats
        setTaskStatusData([
          { name: 'Completed', value: dashboardStats.completedTasks, color: theme.palette.success.main },
          { name: 'Active', value: dashboardStats.activeTasks, color: theme.palette.info.main },
          { name: 'Pending', value: dashboardStats.totalTasks - dashboardStats.completedTasks - dashboardStats.activeTasks, color: theme.palette.warning.main },
        ]);

        setLoadError(
          'Some dashboard analytics could not be loaded, so the figures below may be incomplete.'
        );
      } catch (fallbackError) {
        console.error('Dashboard fallback load also failed:', fallbackError);
        setLoadError(
          'We could not load the dashboard. Check your connection and try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Add time range selector for analytics
  const handleTimeRangeChange = (newRange: string) => {
    setAnalyticsTimeRange(newRange);
  };

  if (loading) {
    return (
      <Box>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* The warm ambient bloom behind the hero row — decorative, so it sits
          out of the tab order and never intercepts a click meant for what's
          on top of it. */}
      <Box sx={heroGlow(theme.palette.mode)} />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
      <PageHeader
        title="Dashboard"
        subtitle="Live view of ninjas, tasks and revenue"
        actions={
          <Button startIcon={<Refresh />} onClick={loadDashboardData} variant="outlined">
            Refresh
          </Button>
        }
      />

      {loadError && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadDashboardData}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Total Ninjas"
            value={dashboardStats.totalNinjas}
            delta={`${performanceMetrics.ninjaGrowth > 0 ? '+' : ''}${performanceMetrics.ninjaGrowth}% from last month`}
            icon={<People />}
            tone="primary"
            deltaDirection={performanceMetrics.ninjaGrowth >= 0 ? "up" : "down"}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Active Tasks"
            value={dashboardStats.activeTasks}
            delta={`${performanceMetrics.taskGrowth > 0 ? '+' : ''}${performanceMetrics.taskGrowth}% from last week`}
            icon={<Assignment />}
            tone="info"
            deltaDirection={performanceMetrics.taskGrowth >= 0 ? "up" : "down"}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Total Revenue"
            value={`₹${dashboardStats.totalRevenue?.toLocaleString() || 0}`}
            delta={`${performanceMetrics.revenueGrowth > 0 ? '+' : ''}${performanceMetrics.revenueGrowth}% from last month`}
            icon={<AttachMoney />}
            tone="success"
            deltaDirection={performanceMetrics.revenueGrowth >= 0 ? "up" : "down"}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Pending Approvals"
            value={dashboardStats.pendingNinjas}
            icon={<PersonAdd />}
            tone="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Revenue Chart */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Revenue Trend
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Time Range</InputLabel>
                    <Select
                      value={analyticsTimeRange}
                      label="Time Range"
                      onChange={(e) => handleTimeRangeChange(e.target.value)}
                    >
                      <MenuItem value="1month">1 Month</MenuItem>
                      <MenuItem value="3months">3 Months</MenuItem>
                      <MenuItem value="6months">6 Months</MenuItem>
                      <MenuItem value="1year">1 Year</MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton size="small" onClick={loadDashboardData}>
                    <Refresh />
                  </IconButton>
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                {revenueData.length > 0 ? (
                  <AreaChart data={revenueData} margin={{ left: -20, right: 12, top: 8 }}>
                    <defs>
                      {/* The warm gradient glow under the line, fading to
                          nothing rather than a flat fill — the signature look
                          of the reference chart. */}
                      <linearGradient id="revenueGlow" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.45} />
                        <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={theme.palette.divider}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      stroke={theme.palette.text.secondary}
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: theme.palette.divider }}
                    />
                    <YAxis
                      stroke={theme.palette.text.secondary}
                      tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                      width={56}
                      tickFormatter={(v) => `₹${v >= 1000 ? `${v / 1000}k` : v}`}
                    />
                    <Tooltip
                      formatter={(value: number) => [`₹${value?.toLocaleString()}`, 'Revenue']}
                      contentStyle={{
                        borderRadius: 12,
                        border: 'none',
                        background: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
                        color: theme.palette.mode === 'dark' ? '#16181D' : '#FFFFFF',
                        boxShadow: '0 12px 28px rgba(0,0,0,0.28)',
                        padding: '8px 12px',
                      }}
                      itemStyle={{ color: 'inherit', fontWeight: 700 }}
                      labelStyle={{ color: 'inherit', opacity: 0.65, fontSize: 12, marginBottom: 2 }}
                      cursor={{ stroke: theme.palette.primary.main, strokeWidth: 1, strokeDasharray: '4 4' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke={theme.palette.primary.main}
                      strokeWidth={3}
                      fill="url(#revenueGlow)"
                      dot={false}
                      activeDot={{
                        r: 6,
                        fill: theme.palette.primary.main,
                        stroke: theme.palette.mode === 'dark' ? '#0B0C10' : '#FFFFFF',
                        strokeWidth: 3,
                      }}
                    />
                  </AreaChart>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    color: 'text.secondary'
                  }}>
                    <AttachMoney sx={{ fontSize: 48, mb: 2 }} />
                    <Typography variant="body2">
                      No revenue data available for selected period
                    </Typography>
                  </Box>
                )}
              </ResponsiveContainer>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                {revenueData.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue ({analyticsTimeRange}): ₹{revenueData.reduce((sum, item) => sum + item.revenue, 0).toLocaleString()}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Task Status Chart */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Task Distribution
              </Typography>
              {taskStatusData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={taskStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {taskStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [value, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ mt: 2 }}>
                    {taskStatusData.map((item) => (
                      <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: item.color,
                            mr: 1,
                          }}
                        />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      Total Tasks: {taskStatusData.reduce((sum, item) => sum + item.value, 0)}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                  <Assignment sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary" align="center">
                    No task data available
                  </Typography>
                  <Button 
                    size="small" 
                    onClick={loadDashboardData} 
                    sx={{ mt: 1 }}
                    startIcon={<Refresh />}
                  >
                    Retry
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Ninja Applications */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Recent Ninja Applications">
            <List disablePadding>
              {recentNinjas.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  No pending applications
                </Typography>
              ) : (
                recentNinjas.map((ninja) => (
                  <ListItem key={ninja.id} sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {(ninja.fullName || '?').charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={ninja.fullName || 'Unnamed ninja'}
                      secondary={`${getSegmentNames(ninja.selectedSegments)} • ${ninja.email}`}
                    />
                    <StatusChip status={ninja.verificationStatus} />
                  </ListItem>
                ))
              )}
            </List>
          </SectionCard>
        </Grid>

        {/* Recent Tasks */}
        <Grid item xs={12} md={6}>
          <SectionCard title="Recent Tasks">
            <List disablePadding>
              {recentTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  No recent tasks
                </Typography>
              ) : (
                recentTasks.map((task) => {
                  // A delivery task's secondary line names the package; a
                  // service task has no packageType at all, only a service
                  // name/category — reading the wrong field was rendering the
                  // literal word "undefined" for every service task here.
                  const kind =
                    task.taskType === 'service'
                      ? task.serviceName || task.category || 'Service'
                      : task.packageType || 'Delivery';
                  return (
                    <ListItem key={task.id} sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          <Assignment />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={task.title}
                        secondary={`${kind} • ₹${task.estimatedPrice}`}
                      />
                      <StatusChip status={task.status} />
                    </ListItem>
                  );
                })
              )}
            </List>
          </SectionCard>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;